<template>
  <div class="form76">
    <div class="form76-header">
      <section class="form76-name">
        <h2>Форма 76</h2>
        <p v-if="data" class="form76-legal">
          Законоустановени работни дни за месеца:
          <strong>{{ data.legalWorkDays }}</strong>
        </p>
      </section>

      <div class="header-controlls">
        <datepicker
          :language="bg"
          :monday-first="true"
          :minimum-view="'month'"
          :maximum-view="'year'"
          :format="'MMMM yyyy'"
          :input-class="'form76-month'"
          v-model="date"
        ></datepicker>
        <div class="btns">
          <a class="btn btn-excel" :href="excelUrl" target="_blank" @click="checkExport($event)">
            <span class="miv">E</span>
            Експорт в Excel
          </a>
        </div>
      </div>
    </div>

    <ul class="form76-legend" v-if="data && data.workers.length">
      <li v-for="(l, i) in legend" :key="`legend-${i}`">
        <strong :class="l.class">{{ l.code }}</strong>
        {{ l.name }}
      </li>
    </ul>

    <div class="table-wrapper" v-if="data && data.workers.length">
      <table class="form76-table">
        <thead>
          <tr>
            <th rowspan="2">№</th>
            <th rowspan="2" class="sticky">Име, презиме, фамилия</th>
            <th
              v-for="(d, i) in data.days"
              :key="`day-${i}`"
              rowspan="2"
              :class="dayClass(d)"
            >
              <template v-if="d">{{ d.day }}</template>
            </th>
            <th
              v-for="s in summary"
              :key="`summary-${s.key}`"
              :rowspan="s.code ? 1 : 2"
              class="summary vertical"
            >
              <span>{{ s.name }}</span>
            </th>
            <th v-if="data.projects.length" :colspan="data.projects.length">
              Часове по проекти (ч.д.)
            </th>
          </tr>
          <tr>
            <template v-for="s in summary">
              <th v-if="s.code" :key="`code-${s.key}`" class="summary">
                {{ s.code }}
              </th>
            </template>
            <th
              v-for="p in data.projects"
              :key="`project-${p._id}`"
              class="vertical"
              :style="projectStyle(p)"
            >
              <span>{{ p.name }}</span>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(w, i) in data.workers" :key="`worker-${w._id}`">
            <td>{{ i + 1 }}</td>
            <td class="sticky name">{{ w.name }}</td>
            <td
              v-for="(c, j) in w.cells"
              :key="`cell-${i}-${j}`"
              :class="dayClass(data.days[j])"
            >
              <template v-if="c">{{ c.label }}</template>
            </td>
            <td
              v-for="s in summary"
              :key="`total-${i}-${s.key}`"
              class="summary"
              :class="{ negative: s.signed && w.totals[s.key] < 0 }"
            >
              {{ w.totals[s.key] }}
            </td>
            <td v-for="p in data.projects" :key="`wp-${i}-${p._id}`">
              {{ w.projects[p._id] || 0 }}
            </td>
          </tr>
        </tbody>
        <tfoot>
          <tr>
            <td></td>
            <td class="sticky name">Общо</td>
            <td v-for="(d, i) in data.days" :key="`tday-${i}`"></td>
            <td v-for="s in summary" :key="`ttotal-${s.key}`">
              <template v-if="s.key !== 'norm'">{{ data.totals[s.key] }}</template>
            </td>
            <td v-for="p in data.projects" :key="`tp-${p._id}`">
              {{ p.total }}
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
    <p v-else-if="data" class="form76-empty">
      За избрания месец няма създадени отчети или отсъствия
    </p>
  </div>
</template>

<script>
import Datepicker from "vuejs-datepicker";
import { bg } from "vuejs-datepicker/dist/locale";
export default {
  components: {
    Datepicker,
  },
  data() {
    return {
      bg: bg,
      date: new Date(),
      data: undefined,
      legend: [
        { code: "8", name: "отработени часове за деня" },
        { code: "-", name: "работен ден без отчет" },
        { code: "Н", name: "почивен/празничен ден без отчет" },
        { code: "О", name: "платен отпуск" },
        { code: "Б", name: "отпуск по болест" },
        { code: "НО", name: "неплатен отпуск" },
        { code: "ФНО", name: "фиктивен неплатен отпуск" },
        { code: "НПР", name: "неприсъствен (извън работния период)" },
        { code: "Б/4", name: "отсъствие и отчет в един ден (код/часове)" },
        { code: "ч.д.", name: "човекодни: до 4 ч = 0.5, над 4 ч = 1" },
        { code: "\u00a0", name: "събота и неделя", class: "day-weekend" },
        { code: "\u00a0", name: "национален празник", class: "day-holiday" },
      ],
      summary: [
        { key: "total", name: "Общо изработени ч.д." },
        { key: "workday", name: "Работни ч.д." },
        { key: "weekend", name: "Събота и неделя ч.д." },
        { key: "holiday", name: "Национален празник ч.д." },
        { key: "paid", name: "Платен отпуск", code: "О" },
        { key: "hospital", name: "Отпуск по болест", code: "Б" },
        { key: "unpaid", name: "Неплатен отпуск", code: "НО" },
        { key: "fake", name: "Фиктивен неплатен отпуск", code: "ФНО" },
        { key: "notpresent", name: "Неприсъствен", code: "НПР" },
        { key: "norm", name: "Индивидуална норма" },
        {
          key: "differenceNorm",
          name: "Разлика м/у изработени дни и инд. норма",
          signed: true,
        },
        {
          key: "differenceHours",
          name: "Разлика на действително изработени и приравнени човекодни",
        },
      ],
    };
  },
  computed: {
    // middle of the month, so the timezone can't move it to another month
    month() {
      const d = new Date(this.date);
      return new Date(d.getFullYear(), d.getMonth(), 15, 12).toISOString();
    },
    excelUrl() {
      const token = localStorage.getItem("token");
      return `${process.env.VUE_APP_BASE_URI}/normative/form76/${this.month}/xlsx?token=${token}`;
    },
  },
  watch: {
    month() {
      this.getData();
    },
  },
  methods: {
    getData() {
      this.$store.dispatch("startLoading");
      const url = `${process.env.VUE_APP_BASE_URI}/normative/form76/${this.month}`;
      this.$http
        .get(url)
        .then((response) => {
          this.data = response.data;
        })
        .catch((error) => {
          console.log(error);
          this.$snotify.error("Нещо се обърка");
        })
        .then(() => this.$store.dispatch("stopLoading"));
    },
    dayClass(d) {
      return d ? `day-${d.type}` : "day-none";
    },
    projectStyle(p) {
      return p.color ? { borderTop: `4px solid ${p.color}` } : {};
    },
    checkExport(e) {
      if (!this.data || !this.data.workers.length) {
        e.preventDefault();
        this.$snotify.warning(
          "За избрания месец няма създадени отчети или отсъствия"
        );
      }
    },
  },
  mounted() {
    this.getData();
  },
};
</script>

<style scoped>
.form76 {
  background-color: #f0f7f7;
  min-height: calc(100vh - 100px);
  padding: 10px 40px;
}
.form76-header {
  align-items: center;
  display: flex;
  justify-content: space-between;
  margin-bottom: 10px;
}
.form76-name {
  align-items: center;
  display: flex;
  gap: 20px;
}
.form76-legal {
  font-size: 13px;
}
.header-controlls {
  align-items: center;
  display: flex;
  gap: 10px;
}
.header-controlls .btns {
  display: flex;
}
.form76 >>> .form76-month {
  background-color: #c9d9d9;
  border: 1px solid #c9d9d9;
  border-radius: 4px;
  cursor: pointer;
  height: 36px;
  padding: 0 10px;
  text-transform: capitalize;
  width: 150px;
}
.form76-legend {
  display: flex;
  flex-wrap: wrap;
  font-size: 12px;
  gap: 4px 18px;
  list-style: none;
  margin: 0 0 10px;
  padding: 0;
}
.form76-legend strong {
  background-color: #fff;
  border: 1px solid #c9d9d9;
  border-radius: 3px;
  display: inline-block;
  margin-right: 4px;
  min-width: 22px;
  padding: 1px 5px;
  text-align: center;
}
.form76-legend .day-weekend {
  background-color: #fce4d6;
}
.form76-legend .day-holiday {
  background-color: #d9f2d0;
}
.form76-empty {
  padding: 40px 0;
  text-align: center;
}
.table-wrapper {
  max-height: calc(100vh - 215px);
  overflow: auto;
}
.form76-table {
  background-color: #fff;
  border-collapse: separate;
  border-spacing: 0;
  font-size: 12px;
}
.form76-table th,
.form76-table td {
  border-bottom: 1px solid #c9d9d9;
  border-right: 1px solid #c9d9d9;
  min-width: 34px;
  padding: 4px 5px;
  text-align: center;
  white-space: nowrap;
}
.form76-table thead th {
  background-color: #e8eeee;
  font-weight: 700;
}
.form76-table .sticky {
  background-color: #fff;
  left: 0;
  position: sticky;
  z-index: 1;
}
.form76-table thead .sticky {
  background-color: #e8eeee;
}
.form76-table .name {
  text-align: left;
}
.form76-table .vertical {
  height: 190px;
  vertical-align: bottom;
}
.form76-table .vertical span {
  display: inline-block;
  max-height: 180px;
  transform: rotate(180deg);
  white-space: normal;
  writing-mode: vertical-rl;
}
/* weekends and holidays are colored no matter if there is a report */
.form76-table .day-weekend,
.form76-table thead .day-weekend {
  background-color: #fce4d6;
}
.form76-table .day-holiday,
.form76-table thead .day-holiday {
  background-color: #d9f2d0;
}
.form76-table .day-none,
.form76-table thead .day-none {
  background-color: #f3f3f3;
}
.form76-table .summary,
.form76-table thead .summary {
  background-color: #cfeefb;
}
.form76-table .negative {
  background-color: #ffc7ce;
  color: #9c0006;
}
.form76-table tfoot td {
  background-color: #deebf7;
  font-weight: 700;
}

@media screen and (max-width: 1000px) {
  .form76 {
    padding: 10px 15px;
  }
  .form76-header {
    align-items: flex-start;
    flex-direction: column;
    gap: 10px;
  }
}
</style>
